import axios from 'axios';
import { getTokenSync, clearSession } from './authStorage';
import { isMobileContainer } from './device';
import { normalizeBackendUrl, DEFAULT_API_BASE } from './apiBase';

// Backend API base. Set REACT_APP_BACKEND_URL in .env (no trailing slash; include /public only if that is the web path to index.php)
const rawBackend = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = normalizeBackendUrl(rawBackend) || DEFAULT_API_BASE;

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  // API_BASE is exported above for building asset URLs
  headers: {
    'Content-Type': 'application/json',
  },
});

// Fix URL: axios drops baseURL path when url starts with /. Strip leading slash
// so baseURL + url = http://localhost:8080/api + auth/login = http://localhost:8080/api/auth/login
api.interceptors.request.use(
  (config) => {
    if (config.url && config.url.startsWith('/')) {
      config.url = config.url.slice(1);
    }
    const token = getTokenSync();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (isMobileContainer()) {
      config.headers['X-App-Client'] = 'mobile';
    }
    // Let browser set multipart boundary (default application/json breaks file uploads)
    if (config.data instanceof FormData) {
      if (typeof config.headers?.delete === 'function') {
        config.headers.delete('Content-Type');
        config.headers.delete('content-type');
      } else {
        delete config.headers['Content-Type'];
        delete config.headers['content-type'];
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle auth errors (don't redirect if the failed request was login - we're already on login page)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const message = error.response?.data?.message || '';
    const isSchoolDeactivated =
      status === 403 &&
      typeof message === 'string' &&
      message.includes('school has been deactivated');
    const isAccountInactive =
      status === 401 &&
      typeof message === 'string' &&
      message.toLowerCase().includes('account is not active');

    if (status === 401 || isSchoolDeactivated) {
      const isAuthRequest = error.config?.url?.includes('/auth/login')
        || error.config?.url?.includes('/auth/academy-sso');
      const hashPath = (() => {
        try {
          const hash = window.location?.hash || '';
          return (hash.replace(/^#/, '').split('?')[0] || '/');
        } catch {
          return '';
        }
      })();
      const isPublicAppScreen = hashPath === '/'
        || hashPath === '/invoice-portal'
        || hashPath.startsWith('/student-card/')
        || hashPath === '/sso'
        || hashPath === '/login';
      if (!isAuthRequest && !isPublicAppScreen) {
        clearSession()
          .catch(() => {})
          .finally(() => {
            window.dispatchEvent(new CustomEvent('schoolerp:auth-cleared'));
            try {
              if ((isSchoolDeactivated || isAccountInactive) && message) {
                sessionStorage.setItem('schoolerp:login_flash', message);
              }
            } catch {
              /* ignore */
            }
            window.location.hash = '#/login';
          });
      }
    }
    return Promise.reject(error);
  }
);

/** Canonical DB path: assets/school/{id}/{filename} */
export function normalizeSchoolAssetPath(path, schoolId) {
  if (path == null || path === '') return null;
  let s = String(path).replace(/\\/g, '/').trim();
  if (!s) return null;

  const hashIdx = s.indexOf('#');
  if (hashIdx >= 0) s = s.slice(0, hashIdx);
  const qIdx = s.indexOf('?');
  if (qIdx >= 0) s = s.slice(0, qIdx);

  if (s.startsWith('http://') || s.startsWith('https://')) {
    const embedded = s.match(/(?:^|\/)assets\/school\/(\d+)\/([^?#]+)/);
    if (embedded) {
      const file = embedded[2].split('/').pop();
      return file ? `assets/school/${embedded[1]}/${file}` : null;
    }
    return null;
  }

  s = s.replace(/^\/+/, '');

  if (s.startsWith('api/assets/school/')) {
    s = s.slice(4);
  }

  const embedded = s.match(/(?:^|\/)assets\/school\/(\d+)\/([^?#]+)/);
  if (embedded) {
    const file = embedded[2].split('/').pop();
    return file ? `assets/school/${embedded[1]}/${file}` : null;
  }

  const sid = schoolId != null && schoolId !== '' ? String(schoolId) : '';
  if (sid && !s.includes('/')) {
    return `assets/school/${sid}/${s}`;
  }

  return null;
}

/** Build GET URL for canonical assets/school/{id}/{file} path. */
export function buildSchoolAssetApiUrl(canonicalPath) {
  if (!canonicalPath) return null;
  const m = String(canonicalPath).match(/^assets\/school\/(\d+)\/(.+)$/);
  if (!m) return null;
  return `${API_BASE}/api/assets/school/${m[1]}/${m[2]}`;
}

/** Absolute asset URL from upload API (asset_url or url field). */
export function resolveUploadAssetDisplayUrl(data, schoolId) {
  if (!data || typeof data !== 'object') return null;
  const direct = data.asset_url;
  if (direct && String(direct).startsWith('http')) return String(direct);
  const fromApiPath = buildSchoolAssetApiUrl(normalizeSchoolAssetPath(data.url, schoolId));
  if (fromApiPath) return fromApiPath;
  return null;
}

/** Parse upload API response — canonical assets/school path for DB/state. */
export function extractUploadedAssetPath(data, columnKey, schoolId) {
  if (!data || typeof data !== 'object') return null;
  const direct = normalizeSchoolAssetPath(data[columnKey], schoolId);
  if (direct) return direct;

  const fromUrl = normalizeSchoolAssetPath(data.url, schoolId);
  if (fromUrl) return fromUrl;

  for (const v of Object.values(data)) {
    if (typeof v !== 'string') continue;
    const normalized = v.replace(/\\/g, '/');
    if (/assets\/school\/\d+\//.test(normalized)) {
      const parsed = normalizeSchoolAssetPath(normalized, schoolId);
      if (parsed) return parsed;
    }
  }
  return null;
}

/** Build full URL for school asset (logo, etc.) stored as assets/school/{id}/filename */
export function getSchoolAssetUrl(logo, schoolId) {
  if (!logo) return null;
  if (String(logo).startsWith('http')) return logo;
  const canonical = normalizeSchoolAssetPath(logo, schoolId);
  if (canonical) return buildSchoolAssetApiUrl(canonical);
  const raw = String(logo).replace(/\\/g, '/').trim();
  if (/^api\/assets\/school\/\d+\//.test(raw)) {
    return `${API_BASE}/${raw}`;
  }
  return null;
}

/** Canonical DB path for student/teacher profile photos. */
export function normalizeProfileAssetPath(avatar) {
  if (avatar == null || avatar === '') return null;
  let s = String(avatar).replace(/\\/g, '/').trim();
  if (!s) return null;

  const qIdx = s.indexOf('?');
  if (qIdx >= 0) s = s.slice(0, qIdx);
  const hashIdx = s.indexOf('#');
  if (hashIdx >= 0) s = s.slice(0, hashIdx);

  if (s.startsWith('http://') || s.startsWith('https://')) {
    const m = s.match(/(?:^|\/)assets\/school\/(\d+)\/(student|teacher)\/(\d+)\/([^?#]+)/);
    return m ? `assets/school/${m[1]}/${m[2]}/${m[3]}/${m[4]}` : null;
  }

  s = s.replace(/^\/+/, '');
  if (s.startsWith('api/assets/school/')) {
    s = s.slice(4);
  }

  const m = s.match(/^assets\/school\/(\d+)\/(student|teacher)\/(\d+)\/([^?#]+)$/);
  if (m) return `assets/school/${m[1]}/${m[2]}/${m[3]}/${m[4]}`;

  const embedded = s.match(/(?:^|\/)assets\/school\/(\d+)\/(student|teacher)\/(\d+)\/([^?#]+)/);
  if (embedded) {
    return `assets/school/${embedded[1]}/${embedded[2]}/${embedded[3]}/${embedded[4]}`;
  }

  return null;
}

export function buildProfileAssetApiUrl(canonicalPath) {
  if (!canonicalPath) return null;
  const m = String(canonicalPath).match(/^assets\/school\/(\d+)\/(student|teacher)\/(\d+)\/(.+)$/);
  if (!m) return null;
  return `${API_BASE}/api/assets/school/${m[1]}/${m[2]}/${m[3]}/${m[4]}`;
}

/** Display URL after profile photo upload (prefers asset_url from API). */
export function resolveUploadProfileDisplayUrl(data) {
  if (!data || typeof data !== 'object') return null;
  if (data.asset_url && String(data.asset_url).startsWith('http')) {
    return String(data.asset_url);
  }
  const fromUrl = buildProfileAssetApiUrl(normalizeProfileAssetPath(data.url));
  if (fromUrl) return fromUrl;
  return buildProfileAssetApiUrl(normalizeProfileAssetPath(data.avatar));
}

/**
 * Build full URL for student/teacher profile photo stored as
 * assets/school/{schoolId}/student/{studentId}/filename or
 * assets/school/{schoolId}/teacher/{teacherId}/filename
 */
export function getProfileAssetUrl(avatar, cacheKey = '') {
  if (!avatar) return null;
  if (String(avatar).startsWith('http')) {
    const base = String(avatar);
    if (!cacheKey) return base;
    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}t=${encodeURIComponent(cacheKey)}`;
  }
  const canonical = normalizeProfileAssetPath(avatar);
  let url = canonical ? buildProfileAssetApiUrl(canonical) : null;
  if (!url) {
    const raw = String(avatar).replace(/\\/g, '/').trim();
    if (/^api\/assets\/school\/\d+\/(student|teacher)\/\d+\//.test(raw)) {
      url = `${API_BASE}/${raw}`;
    }
  }
  if (!url) return null;
  if (!cacheKey) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}t=${encodeURIComponent(cacheKey)}`;
}

/** Canonical DB path: assets/platform/{filename} */
export function normalizePlatformAssetPath(path) {
  if (path == null || path === '') return null;
  let s = String(path).replace(/\\/g, '/').trim();
  if (!s) return null;

  const qIdx = s.indexOf('?');
  if (qIdx >= 0) s = s.slice(0, qIdx);
  const hashIdx = s.indexOf('#');
  if (hashIdx >= 0) s = s.slice(0, hashIdx);

  if (s.startsWith('http://') || s.startsWith('https://')) {
    const embedded = s.match(/(?:^|\/)assets\/platform\/([^?#]+)/);
    if (embedded) return `assets/platform/${embedded[1]}`;
    return null;
  }

  s = s.replace(/^\/+/, '');
  if (s.startsWith('api/assets/platform/')) {
    s = s.slice('api/assets/platform/'.length);
  }

  if (/^assets\/platform\//.test(s)) return s;
  if (!s.includes('/')) return `assets/platform/${s}`;

  return null;
}

export function buildPlatformAssetApiUrl(canonicalPath) {
  if (!canonicalPath) return null;
  const m = String(canonicalPath).match(/^assets\/platform\/(.+)$/);
  if (!m) return null;
  return `${API_BASE}/api/assets/platform/${m[1]}`;
}

export function getPlatformAssetUrl(logo, cacheKey = '') {
  if (!logo) return null;
  if (String(logo).startsWith('http')) {
    const base = String(logo);
    if (!cacheKey) return base;
    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}t=${encodeURIComponent(cacheKey)}`;
  }
  const canonical = normalizePlatformAssetPath(logo);
  let url = canonical ? buildPlatformAssetApiUrl(canonical) : null;
  if (!url) {
    const raw = String(logo).replace(/\\/g, '/').trim();
    if (/^api\/assets\/platform\//.test(raw)) {
      url = `${API_BASE}/${raw}`;
    }
  }
  if (!url) return null;
  if (!cacheKey) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}t=${encodeURIComponent(cacheKey)}`;
}

/** Resolve user avatar for platform CRM paths or school student/teacher paths. */
export function getUserAvatarUrl(avatar, cacheKey = '') {
  if (!avatar) return null;
  if (String(avatar).startsWith('http://') || String(avatar).startsWith('https://')) {
    const base = String(avatar);
    if (!cacheKey) return base;
    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}t=${encodeURIComponent(cacheKey)}`;
  }
  return getPlatformAssetUrl(avatar, cacheKey) || getProfileAssetUrl(avatar, cacheKey);
}

export function extractUploadedPlatformAssetPath(data, columnKey = 'logo') {
  if (!data || typeof data !== 'object') return null;
  const direct = normalizePlatformAssetPath(data[columnKey]);
  if (direct) return direct;

  const fromUrl = normalizePlatformAssetPath(data.url);
  if (fromUrl) return fromUrl;

  for (const v of Object.values(data)) {
    if (typeof v !== 'string') continue;
    const normalized = v.replace(/\\/g, '/');
    if (/assets\/platform\//.test(normalized)) {
      const parsed = normalizePlatformAssetPath(normalized);
      if (parsed) return parsed;
    }
  }
  return null;
}

/** Build authenticated CRM chat attachment/thumbnail URL from API path. */
export function getCrmChatAssetUrl(apiPath) {
  if (!apiPath) return null;
  if (apiPath.startsWith('http://') || apiPath.startsWith('https://')) return apiPath;
  const clean = apiPath.startsWith('/') ? apiPath.slice(1) : apiPath;
  return `${API_BASE}/api/${clean}`;
}

export default api;
